import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconArrowBackUp, IconDeviceFloppy } from '@tabler/icons-react';
import { Button, Card, Center, Loader } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import PageContent from '../../../../components/core/PageContent/PageContent';
import servicesService from '../../../../services/core/services.service';
import { FormView, FormViewData } from './components/FormView';
import { ServiceType } from '../../../../models/core/services.type';
import { Feature } from '../../../../utils/constants.utils';
import { Permission } from '../../../../models/core/departments.type';

type DataResult = {
  loading: boolean;
  serviceData: ServiceType | null;
};

function ServiceAddEdit() {
  const navigate = useNavigate();
  const location: any = useLocation();
  const { idServico } = useParams();

  const [dataResult, setDataResult] = useState<DataResult>({
    loading: (!!idServico && !location?.state?.service) || false,
    serviceData: location?.state?.service || null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let { serviceData } = dataResult;
      try {
        serviceData = await servicesService.select({ idServico: Number(idServico) });
      } catch (error: any) {
        showNotification({
          title: `Serviço - ${!idServico ? 'Adicionar' : 'Editar'}`,
          message: error?.isBusinessException ? error.description : 'Não foi possível carregar o serviço.',
          color: 'red',
        });
        navigate(`/${error?.statusCode || '500'}`, {
          replace: true,
        });
      } finally {
        setDataResult({ loading: false, serviceData });
      }
    };

    if (idServico && dataResult.serviceData === null) {
      fetchData();
    }
  }, [idServico, navigate, dataResult]);

  const save = async (inputFormViewData: FormViewData) => {
    try {
      setSaving(true);
      let newModel;
      if (!idServico) {
        newModel = await servicesService.insert({
          ...inputFormViewData,
        });
        navigate('/services');
      } else {
        await servicesService.update({
          ...inputFormViewData,
          idServico: Number(idServico),
        });
      }
      showNotification({
        title: `Serviço - ${!idServico ? 'Adicionar' : 'Editar'}`,
        message: 'Serviço salvo com sucesso.',
        color: 'green',
      });
      navigate(`/services/${newModel?.idServico || idServico}`, {
        state: {
          service: newModel || null,
        },
      });
    } catch (error: any) {
      showNotification({
        title: `Serviço - ${!idServico ? 'Adicionar' : 'Editar'}`,
        message: error?.isBusinessException ? error.description : 'Não foi possível salvar o serviço.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Service}
        title={`Serviço - ${!idServico ? 'Adicionar' : 'Editar'}`}
        description={`${!idServico ? 'Adicione' : 'Edite'} um serviço.`}
        buttons={[
          <Button
            key="Voltar"
            color="secondary"
            leftSection={<IconArrowBackUp size={18} />}
            onClick={() => {
              navigate(-1);
            }}
            disabled={dataResult.loading || saving}
          >
            Voltar
          </Button>,
          <Button
            key="Salvar"
            color="primary"
            type="submit"
            form="service-add-edit"
            leftSection={<IconDeviceFloppy size={18} />}
            disabled={dataResult.loading}
            loading={saving}
            data-permission={!idServico ? Permission.ServiceAdd : Permission.ServiceEdit}
          >
            Salvar
          </Button>,
        ]}
      />
      {idServico && dataResult.serviceData === null ? (
        <Center>
          <Loader size="xl" />
        </Center>
      ) : (
        <PageContent>
          <FormView service={dataResult.serviceData} loading={saving} save={save} />
        </PageContent>
      )}
    </Card>
  );
}

export default ServiceAddEdit;
